<template>
  <section class="contact-details">
    <div v-if="!contactDetails.disableHeader">
      <h2>{{ contactDetails.title }}</h2>
      <p v-html="contactDetails.description"></p>
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <Checkbox
            v-model:checked="allLocationsCheckbox"
            id="contactDetailsCheckbox"
            class="border-secondary me-2"
          />
          <label for="contactDetailsCheckbox" class="user-select-none">{{
            contactDetails.label
          }}</label>
        </div>
        <ButtonRed
          hyperlink
          :href="contactDetails.button.link"
          :text="contactDetails.button.text"
          class="user-select-none"
          v-if="!shouldShowRedButton"
        />
      </div>
    </div>
    <div class="map-container" :class="{ container: !shouldShowRedButton }">
      <div class="row">
        <ul class="map-list col-12 col-lg-4 order-1 order-lg-0">
          <ContactCard
            v-if="locations.length > 0"
            v-for="(contactLocation, index) in locations"
            :contactLocation="contactLocation"
            :buttons="{
              service: contactDetails.iconsDescription.service,
              screwDriverIcon: contactDetails.iconsDescription.screwDriver,
            }"
            :key="contactLocation.id"
            @mouseover="handleInfoWindow(index, true)"
            @mouseleave="handleInfoWindow(index, false)"
            :class="{ highlight: infoWindow[index]?.cardHighlight }"
          />
        </ul>
        <div class="map col-12 col-lg-8 order-0 order-lg-1">
          <GMapMap
            :center="center"
            map-type-id="terrain"
            class="g-map-map mw-100"
            :options="options"
            @map-resolved="mapResolved"
            :mapPlaceholderImage="contactDetails.mapPlaceholderImage"
          >
            <GMapMarker
              :key="location.marker.id"
              v-for="(location, index) in locations"
              :position="location.marker.position"
              @mouseover="handleInfoWindow(index, true, 'over')"
              @mouseout="handleInfoWindow(index, false, 'out')"
            >
              <GMapInfoWindow :opened="infoWindow[index]?.status || false">
                <p class="mb-0">{{ infoWindow[index]?.title || "" }}</p>
              </GMapInfoWindow>
            </GMapMarker>
            <GMapBounds
              :positions="locations.map((location) => location.marker.position)"
            />
          </GMapMap>
        </div>
      </div>
    </div>
    <div
      class="map-icons-description d-flex flex-column flex-lg-row align-items-lg-center container"
    >
      <div
        class="d-flex align-items-center mb-3 mb-lg-0"
        v-if="contactDetails.iconsDescription.screwDriver"
      >
        <img
          :src="screwDriverIconPng"
          alt="screwDriverIconPng"
          width="30"
          height="24"
        />
        <p class="ms-3">{{ contactDetails.iconsDescription.screwDriver }}</p>
      </div>
      <div
        class="d-flex align-items-center"
        v-if="contactDetails.iconsDescription.service"
      >
        <img
          :src="serviceIconPng"
          alt="serviceIconPng"
          width="24"
          height="24"
        />
        <p class="ms-3">{{ contactDetails.iconsDescription.service }}</p>
      </div>
    </div>
    <div class="container">
      <ButtonRed
        hyperlink
        :href="contactDetails.button.link"
        :text="contactDetails.button.text"
        class="user-select-none w-100 mt-4 mobile-button"
        v-if="shouldShowRedButton && contactDetails.button"
      />
    </div>
  </section>
</template>

<script>
import ButtonRed from "@/components/common/ButtonRed";
import ContactCard from "@/components/сontact-details-map/ContactCard";
import Checkbox from "@/components/common/Checkbox";
import ScrewDriverIcon from "@/assets/icons/screw-driver-icon";
import ServiceIcon from "@/assets/icons/service-icon";
import api from "@/config/api";
import { handleListeners } from "@/utils/utils";
import useLoadData from "@/composables/useLoadData";
import ServiceIconPng from "@/assets/images/service-icon.png";
import ScrewDriverIconPng from "@/assets/images/screw-driver.png";

export default {
  components: {
    ServiceIcon,
    ScrewDriverIcon,
    Checkbox,
    ContactCard,
    ButtonRed,
  },
  props: {
    contactDetails: {
      disableHeader: Boolean,
      title: String,
      description: String,
      locationIcon: String,
      label: String,
      button: {
        text: String,
        link: String,
      },
      iconsDescription: {
        screwDriver: String,
        service: String,
      },
      mapPlaceholderImage: String,
      map: {
        zoom: Number,
        showAll: Boolean,
      },
    },
    requestUrl: String,
    requestOnMount: Boolean,
  },
  $resolveMapPromise: null,
  $mapPromise: null,
  data() {
    return {
      locations: [],
      center: {},
      options: {
        disableDefaultUI: true,
      },
      infoWindow: {},
      shouldShowRedButton: false,
      allLocationsCheckbox: false,
      serviceIconPng: ServiceIconPng,
      screwDriverIconPng: ScrewDriverIconPng,
    };
  },
  methods: {
    handleInfoWindow(index, show, mouseOverTheMarker) {
      mouseOverTheMarker === "over" &&
        (this.infoWindow[index].cardHighlight = true);
      mouseOverTheMarker === "out" &&
        (this.infoWindow[index].cardHighlight = false);
      this.infoWindow[index].status = show;
    },
    async fetchLocations(config) {
      const { data: locations, fetchData: fetchContactLocations } = useLoadData(
        this.$props.requestUrl,
        { method: "get" }
      );
      await fetchContactLocations(config);

      this.locations = locations.value;
      this.center = {
        lat: +this.locations[0].marker.position.lat,
        lng: +this.locations[0].marker.position.lng,
      };
    },
    handleScreenWidth() {
      this.shouldShowRedButton =
        window.matchMedia("(max-width: 992px)").matches;
    },
    initStateForMap() {
      this.locations.forEach((contactLocation, index) => {
        Object.assign(this.infoWindow, {
          [index]: {
            status: false,
            title: contactLocation.locationName,
            cardHighlight: false,
          },
        });
      });
      if (this.locations.length <= 1) {
        this.$mapPromise.then((m) =>
          m.setZoom(this.$props.contactDetails.map.zoom)
        );
      }
    },
    mapResolved(map) {
      this.$resolveMapPromise(map);
    },
  },
  created() {
    this.$mapPromise = new Promise((resolve, _) => {
      this.$resolveMapPromise = resolve;
    });

    if (this.$props.contactDetails.map.showAll) {
      this.allLocationsCheckbox = true;
      return;
    }

    this.$props.requestOnMount && this.fetchLocations();
  },
  mounted() {
    handleListeners(["resize", "DOMContentLoaded"], this.handleScreenWidth);
  },
  unmounted() {
    handleListeners(
      ["resize", "DOMContentLoaded"],
      this.handleScreenWidth,
      true
    );
  },
  watch: {
    allLocationsCheckbox(newValue) {
      this.fetchLocations({
        url: newValue
          ? api.home.map.contactDetailsLocations.all
          : api.home.map.contactDetailsLocations.default,
      });
    },
    requestUrl() {
      this.fetchLocations();
    },
    locations() {
      this.initStateForMap();
    },
  },
};
</script>
